import { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { CancelIcon, DeleteIcon, SubmitIcon } from 'src/v3/core/icons'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  CREDENTIAL_TYPES,
  RecertificationCycleTemplateTypes,
} from 'src/v3/core/types'
import {
  fetchStates,
  selectStates,
  selectStatesStatus,
  StatesResponse,
} from 'src/v3/settings/settings.slice'
import { LICENSE_TYPES, NREMT_TYPES } from 'src/v3/utilities/template-types'

// A template for "Other" would match every custom credential, so it isn't offered.
let CREDENTIAL_OPTIONS = CREDENTIAL_TYPES.filter((t) => t !== 'Other')

let STATE_GROUPS: (keyof StatesResponse)[] = [
  'States',
  'Territories',
  'Miscellaneous',
  'Countries',
]

let EMPTY_TYPES: RecertificationCycleTemplateTypes = {
  nremt: [],
  credentials: [],
  licenses: [],
}

type Props = {
  templateID: string
  types?: RecertificationCycleTemplateTypes
  open: boolean
  onClose: () => void
  onSaved: () => void
}

export default function TemplateTypesForm({
  templateID,
  types,
  open,
  onClose,
  onSaved,
}: Props) {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()
  let states = useAppSelector(selectStates)
  let statesStatus = useAppSelector(selectStatesStatus)

  let [draft, setDraft] = useState(types ?? EMPTY_TYPES)
  let [saving, setSaving] = useState(false)
  let [confirmingAnyState, setConfirmingAnyState] = useState(false)

  useEffect(() => {
    if (statesStatus === 'uninitialized') dispatch(fetchStates())
  }, [statesStatus])

  useEffect(() => {
    if (open) setDraft(types ?? EMPTY_TYPES)
  }, [open, templateID, types])

  let stateOptions = STATE_GROUPS.flatMap((group) =>
    (states?.[group] ?? []).map((state) => ({ group, state })),
  )
  let anyStateLicenses = draft.licenses.filter((l) => l.states.length === 0)
  let unusedLicenseTypes = LICENSE_TYPES.filter(
    (t) =>
      !draft.licenses.some((l) => l.type.toLowerCase() === t.toLowerCase()),
  )

  let toggle = (list: string[], value: string) =>
    list.some((v) => v.toLowerCase() === value.toLowerCase())
      ? list.filter((v) => v.toLowerCase() !== value.toLowerCase())
      : [...list, value]

  let setLicenseStates = (type: string, selected: string[]) =>
    setDraft((d) => ({
      ...d,
      licenses: d.licenses.map((l) =>
        l.type === type ? { type, states: selected } : l,
      ),
    }))

  let save = () => {
    setConfirmingAnyState(false)
    setSaving(true)
    axios
      .put<RecertificationCycleTemplateTypes>(
        `/api/manage/recertification-cycle/template/${templateID}/types`,
        {
          nremt: NREMT_TYPES.filter((t) =>
            draft.nremt.some((n) => n.toLowerCase() === t.toLowerCase()),
          ),
          credentials: CREDENTIAL_OPTIONS.filter((t) =>
            draft.credentials.some((c) => c.toLowerCase() === t.toLowerCase()),
          ),
          licenses: draft.licenses,
        },
      )
      .then(pluckData)
      .then(() => {
        onSaved()
        onClose()
      })
      .catch((err) => showError?.(pluckError(err, 'Error saving types')))
      .finally(() => setSaving(false))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Paper sx={{ px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          Template Types
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            A template with no types is available to every cycle. With types,
            only cycles for one of them can apply it.
          </Typography>

          <Box>
            <Typography variant="subtitle2">NREMT</Typography>
            <FormGroup row>
              {NREMT_TYPES.map((t) => (
                <FormControlLabel
                  key={t}
                  label={t}
                  control={
                    <Checkbox
                      checked={draft.nremt.some(
                        (n) => n.toLowerCase() === t.toLowerCase(),
                      )}
                      onChange={() =>
                        setDraft((d) => ({ ...d, nremt: toggle(d.nremt, t) }))
                      }
                    />
                  }
                />
              ))}
            </FormGroup>
            <Typography color="text.secondary" variant="caption">
              Matched by the first letter of the user's NREMT number.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2">Credentials</Typography>
            <FormGroup row>
              {CREDENTIAL_OPTIONS.map((t) => (
                <FormControlLabel
                  key={t}
                  label={t}
                  control={
                    <Checkbox
                      checked={draft.credentials.some(
                        (c) => c.toLowerCase() === t.toLowerCase(),
                      )}
                      onChange={() =>
                        setDraft((d) => ({
                          ...d,
                          credentials: toggle(d.credentials, t),
                        }))
                      }
                    />
                  }
                />
              ))}
            </FormGroup>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2">Licenses</Typography>
            {draft.licenses.map((license) => (
              <Box
                key={license.type}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
              >
                <Autocomplete
                  sx={{ flexGrow: 1, bgcolor: 'background.paper' }}
                  multiple
                  disableCloseOnSelect
                  limitTags={4}
                  options={stateOptions}
                  groupBy={(opt) => opt.group}
                  getOptionLabel={(opt) => opt.state}
                  isOptionEqualToValue={(opt, val) => opt.state === val.state}
                  value={stateOptions.filter((opt) =>
                    license.states.includes(opt.state),
                  )}
                  onChange={(_, selected) =>
                    setLicenseStates(
                      license.type,
                      selected.map((opt) => opt.state),
                    )
                  }
                  loading={statesStatus === 'pending'}
                  renderOption={(props, opt, { selected }) => (
                    <li {...props} key={opt.state}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1 }}
                      />
                      {opt.state}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${license.type} States`}
                      placeholder={
                        license.states.length === 0 ? 'Any state' : undefined
                      }
                      helperText={
                        license.states.length === 0
                          ? 'No states selected: applies in any state.'
                          : undefined
                      }
                    />
                  )}
                />
                <Button
                  size="small"
                  sx={{ mt: 1.5, whiteSpace: 'nowrap' }}
                  onClick={() =>
                    setLicenseStates(license.type, states?.States ?? [])
                  }
                >
                  All U.S. States
                </Button>
                <IconButton
                  color="error"
                  aria-label={`Remove ${license.type}`}
                  sx={{ mt: 1 }}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      licenses: d.licenses.filter(
                        (l) => l.type !== license.type,
                      ),
                    }))
                  }
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {unusedLicenseTypes.length > 0 && (
              <TextField
                select
                label="Add license type"
                value=""
                sx={{ bgcolor: 'background.paper' }}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    licenses: [
                      ...d.licenses,
                      { type: e.target.value, states: [] },
                    ],
                  }))
                }
              >
                {unusedLicenseTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}
        >
          <Button
            color="primary"
            variant="contained"
            sx={{ flex: '1 1 0px' }}
            startIcon={<SubmitIcon />}
            loading={saving}
            onClick={() =>
              anyStateLicenses.length > 0 ? setConfirmingAnyState(true) : save()
            }
          >
            Submit
          </Button>
          <Button
            color="error"
            onClick={onClose}
            variant="contained"
            sx={{ flex: '1 1 0px' }}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
        </DialogActions>
        {NotificationComponent}
      </Paper>

      <Dialog
        open={confirmingAnyState}
        onClose={() => setConfirmingAnyState(false)}
        maxWidth="xs"
      >
        <DialogTitle>Apply in Any State?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            No states are selected for{' '}
            <strong>{anyStateLicenses.map((l) => l.type).join(', ')}</strong>,
            so this template will be available to those licenses in every state.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setConfirmingAnyState(false)}>Back</Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
